/* eslint-disable react-hooks/exhaustive-deps */
import { ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { setOnStorage } from '@libs/utils/helpers/handleStorage';
import { MatchMakerPayload, useMM } from '../../../services/matchMaker';
import { useCreditTrack } from '../../../hooks/useCreditTrack';
import { useSortingHat } from '../../../services/sortingHat';

import { MM_STORAGE } from '../constants';
import { AnnuityStep, BenefitsStep, MonthlyExpenseStep } from './steps';

import { MMFormProps } from '.';
import { FieldValues } from 'react-hook-form';

type Step = {
  [key: number]: {
    slug: string;
    label: string;
    component: ReactNode;
  };
};

export function useMMForm({ user, token, negativeDebts, onFinish }: MMFormProps) {
  const navigate = useNavigate();
  const { getComparedCards } = useMM({ token });
  const { formViewedEvent, formBackedEvent, formContinuedEvent, formSubmittedEvent, formErroredEvent } = useCreditTrack();
  const { shTrack } = useSortingHat({ user });

  const [stepNumber, setStepNumber] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [resultLoading, setResultLoading] = useState<boolean>(false);

  const steps: Step = {
    1: {
      slug: 'monthly-expense',
      label: 'Gasto mensal',
      component: <MonthlyExpenseStep negativeDebts={negativeDebts} />,
    },
    2: { slug: 'benefits', label: 'Benefícios', component: <BenefitsStep /> },
    3: { slug: 'annuity', label: 'Anuidade', component: <AnnuityStep /> },
  };

  const currentStep = steps[stepNumber];
  const totalSteps = Object.keys(steps).length;

  const tracking = {
    formContext: {
      formBrand: 'match-maker',
      formName: 'match-maker',
      formId: 'match-maker',
      formType: 'default',
      formVersion: 'v2',
    },
    stepContext: {
      stepId: `match-maker-${currentStep.slug}`,
      stepName: currentStep.label,
      stepNumber,
    },
  };

  useEffect(() => {
    if (loading || negativeDebts) return;
    if (stepNumber === 1) shTrack();

    formViewedEvent(tracking);
  }, [stepNumber, loading]);

  useEffect(() => {
    if (!token) return;
    setLoading(false);

  }, [token]);

  const nextStep = () => {
    setStepNumber(stepNumber + 1);
  };

  const prevStep = () => {
    formBackedEvent(tracking);

    if (stepNumber === 1) return navigate('/');

    setStepNumber(stepNumber - 1);
  };

  const onSubmit = async (data: MatchMakerPayload | FieldValues) => {
    const capturedData = Object.keys(data).map(([field, value]) => ({
      fieldId: field,
      fieldName: field,
      fieldValue: value,
    }));
    formContinuedEvent({ formContext: tracking, capturedData });

    const finishForm = stepNumber === totalSteps;
    if (finishForm) {
      formSubmittedEvent({ formContext: tracking, allFields: capturedData });
      setOnStorage('session', MM_STORAGE.FORM, data);
      return await handleResult();
    }

    nextStep();
  };

  async function handleResult() {
    try {
      setResultLoading(true);
      await getComparedCards();
      onFinish?.();
    } catch (error) {
      formErroredEvent({ formContext: tracking, errorMessage: error });
      console.error(error);
    } finally {
      setResultLoading(false);
    }
  }

  return {
    nextStep,
    prevStep,
    onSubmit,
    loading,
    stepNumber,
    currentStep,
    totalSteps,
    resultLoading,
  };
}
